// Ciclo de vida da saudação do Nexo na página de boas-vindas (Zustand, SÓ sessão/UI — não persiste).
//
// Objetivo: a saudação e a fase da animação vivem FORA do componente para que voltar à home depois de abrir
// uma aba (a home desmonta no keep-alive) NÃO reinicie o aceno nem re-saúde. Uma abertura NOVA (outro
// usuário sem reload, ou um reload que zera o store) pode iniciar uma nova saudação. Limpo no logout.
//
// Fases: "waiting" (acenando, aguardando resposta) → "reacting" (pose da resposta) → "breathing" (repouso).
import { create } from "zustand";
import type { DayPeriod } from "@/lib/staffWelcome";

export type WelcomeResponse = "bem" | "corrido" | "trabalhar" | "ajuda";
export type WelcomePhase = "waiting" | "reacting" | "breathing";

type NexoWelcomeState = {
  /** ID ESTÁVEL da conta dona da saudação atual — troca de dono reinicia (não mistura contas). */
  ownerId: string | null;
  period: DayPeriod | null;
  greeting: string | null;
  phase: WelcomePhase;
  response: WelcomeResponse | null;
  /** Mensagem breve exibida após a resposta (acolhimento). */
  reactionMessage: string | null;

  /** Inicia a saudação para um usuário (por ID) se ainda não houver uma para ele (idempotente por dono). */
  ensureGreeting: (accountId: string, period: DayPeriod, greeting: string) => void;
  /** Registra a resposta escolhida e entra na fase de reação com a mensagem breve. */
  react: (response: WelcomeResponse, message: string) => void;
  /** Assenta na respiração (repouso) após a reação breve. */
  settle: () => void;
  /** Limpa tudo (logout / troca de sessão). */
  clear: () => void;
};

export const useNexoWelcome = create<NexoWelcomeState>((set, get) => ({
  ownerId: null,
  period: null,
  greeting: null,
  phase: "waiting",
  response: null,
  reactionMessage: null,

  ensureGreeting: (accountId, period, greeting) => {
    if (get().ownerId === accountId && get().greeting) return;
    set({
      ownerId: accountId,
      period,
      greeting,
      phase: "waiting",
      response: null,
      reactionMessage: null,
    });
  },

  react: (response, message) => set({ phase: "reacting", response, reactionMessage: message }),

  settle: () => set((s) => (s.phase === "reacting" ? { phase: "breathing" } : s)),

  clear: () =>
    set({
      ownerId: null,
      period: null,
      greeting: null,
      phase: "waiting",
      response: null,
      reactionMessage: null,
    }),
}));
